import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CustomDomainResult {
  isCustomDomain: boolean;
  supplierSlug: string | null;
  supplierName: string | null;
  isLoading: boolean;
  error: string | null;
}

// Base domains that are always known (exact match or any subdomain)
const BASE_KNOWN_DOMAINS = [
  'localhost',
  'lovable.app',
  'lovable.dev',
  'lovableproject.com'
];

// Generic whitelabel hosts: not bound to a single supplier — let React Router
// resolve the supplier via the URL slug (e.g., /loja/:slug).
const GENERIC_WHITELABEL_HOSTS = [
  'amecardapio.com',
  'www.amecardapio.com',
];

// Reserved ameup.com.br subdomains (NOT supplier stores)
const RESERVED_AMEUP_SUBDOMAINS = [
  'www',
  'send',
  'api',
  'admin',
  'app'
];

// Check if hostname is a known/internal domain
const isKnownDomain = (hostname: string): boolean => {
  // Generic whitelabel hosts behave like known domains: routing is resolved
  // by the URL slug, not by a domain → supplier mapping.
  if (GENERIC_WHITELABEL_HOSTS.includes(hostname)) {
    return true;
  }

  // Check base known domains (exact match or subdomains)
  if (BASE_KNOWN_DOMAINS.some(known => 
    hostname === known || hostname.endsWith(`.${known}`)
  )) {
    return true;
  }

  // Check Lovable preview pattern
  if (hostname.includes('-preview--') || hostname.includes('lovableproject.com')) {
    return true;
  }

  // Check root ameup.com.br
  if (hostname === 'ameup.com.br') {
    return true;
  }

  // Check ameup.com.br subdomains
  if (hostname.endsWith('.ameup.com.br')) {
    const subdomain = hostname.replace('.ameup.com.br', '');
    // Reserved subdomains are known (not supplier stores)
    if (RESERVED_AMEUP_SUBDOMAINS.includes(subdomain)) {
      return true;
    }
    // Non-reserved subdomains are supplier stores resolved by slug (not custom domain lookup)
    // Return true here so they skip the custom domain DB query — they'll be handled separately
    return false;
  }

  return false;
};

// Cache for custom domain lookups to avoid repeated queries
const domainCache = new Map<string, { slug: string; name: string } | null>();

// Cache persistente (24h) do domínio → loja: quem volta (ou abre o link do Instagram de novo)
// não espera a consulta; revalidamos em segundo plano.
const PERSIST_KEY = 'ameup_custom_domain_cache_v1';
const PERSIST_TTL_MS = 24 * 60 * 60 * 1000;

const readPersistedDomain = (hostname: string): { slug: string; name: string } | null => {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw) as Record<string, { slug: string; name: string; at: number }>;
    const hit = all[hostname];
    if (!hit || Date.now() - hit.at > PERSIST_TTL_MS) return null;
    return { slug: hit.slug, name: hit.name };
  } catch {
    return null;
  }
};

const writePersistedDomain = (hostname: string, value: { slug: string; name: string }) => {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    all[hostname] = { ...value, at: Date.now() };
    localStorage.setItem(PERSIST_KEY, JSON.stringify(all));
  } catch { /* storage indisponível: segue sem cache */ }
};

const resolveActiveCustomDomain = async (hostname: string): Promise<{ slug: string; name: string } | null> => {
  // Resolve through a deliberately narrow public RPC. The underlying domain
  // table remains private and the browser only receives public store identity.
  const { data, error } = await supabase.rpc('resolve_supplier_custom_domain', {
    p_hostname: hostname,
  });

  if (error) {
    console.error('Error resolving custom domain:', error);
    return null;
  }

  const supplier = data?.[0];
  if (!supplier?.slug) {
    return null;
  }

  return {
    slug: supplier.slug,
    name: supplier.name,
  };
};

export const useCustomDomainRouter = (): CustomDomainResult => {
  const [result, setResult] = useState<CustomDomainResult>({
    isCustomDomain: false,
    supplierSlug: null,
    supplierName: null,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    const checkCustomDomain = async () => {
      try {
        const hostname = window.location.hostname;
        
        // If it's a known domain, skip the check
        if (isKnownDomain(hostname)) {
          setResult({
            isCustomDomain: false,
            supplierSlug: null,
            supplierName: null,
            isLoading: false,
            error: null
          });
          return;
        }

        // Check cache first
        if (domainCache.has(hostname)) {
          const cached = domainCache.get(hostname);
          setResult({
            isCustomDomain: cached !== null,
            supplierSlug: cached?.slug || null,
            supplierName: cached?.name || null,
            isLoading: false,
            error: null
          });
          return;
        }

        const persisted = readPersistedDomain(hostname);
        if (persisted) {
          domainCache.set(hostname, persisted);
          setResult({
            isCustomDomain: true,
            supplierSlug: persisted.slug,
            supplierName: persisted.name,
            isLoading: false,
            error: null
          });
          // Revalida em segundo plano (domínio trocado/desativado atualiza o cache).
          resolveActiveCustomDomain(hostname).then((fresh) => {
            if (fresh) {
              domainCache.set(hostname, fresh);
              writePersistedDomain(hostname, fresh);
            }
          }).catch(() => {});
          return;
        }

        const customDomainSupplier = await resolveActiveCustomDomain(hostname);

        if (customDomainSupplier) {
          domainCache.set(hostname, customDomainSupplier);
          writePersistedDomain(hostname, customDomainSupplier);
          setResult({
            isCustomDomain: true,
            supplierSlug: customDomainSupplier.slug,
            supplierName: customDomainSupplier.name,
            isLoading: false,
            error: null
          });
          return;
        }

        // Check if this is a *.ameup.com.br subdomain — resolve by slug directly
        if (hostname.endsWith('.ameup.com.br')) {
          const subdomain = hostname.replace('.ameup.com.br', '');
          const { data: supplier, error: supplierError } = await (supabase as any)
            .from('suppliers_public')
            .select('slug, name')
            .eq('slug', subdomain)
            .maybeSingle();

          if (supplierError || !supplier) {
            domainCache.set(hostname, null);
            setResult({
              isCustomDomain: false,
              supplierSlug: null,
              supplierName: null,
              isLoading: false,
              error: 'Fornecedor não encontrado'
            });
            return;
          }

          domainCache.set(hostname, { slug: supplier.slug || '', name: supplier.name });
          setResult({
            isCustomDomain: true,
            supplierSlug: supplier.slug,
            supplierName: supplier.name,
            isLoading: false,
            error: null
          });
          return;
        }

        domainCache.set(hostname, null);
        setResult({
          // Unknown external hosts are still custom domains. This ensures the
          // wrapper renders its neutral domain error instead of the generic
          // white-label landing page.
          isCustomDomain: true,
          supplierSlug: null,
          supplierName: null,
          isLoading: false,
          error: 'Domínio não configurado'
        });

      } catch (err) {
        console.error('Error checking custom domain:', err);
        setResult({
          isCustomDomain: false,
          supplierSlug: null,
          supplierName: null,
          isLoading: false,
          error: 'Erro ao verificar domínio'
        });
      }
    };

    checkCustomDomain();
  }, []);

  return result;
};

// Utility to clear cache (useful for testing)
export const clearDomainCache = () => {
  domainCache.clear();
};
